import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gauge, ShieldAlert, Sparkles, FileDown, CalendarClock, TrendingDown, TrendingUp, Minus } from "lucide-react";

const BAND_COLORS = {
  ok:     { bar: "bg-emerald-500", tag: "hs-tag-success", label: "OK" },
  warn:   { bar: "bg-amber-500",   tag: "hs-tag-warn",    label: "WATCH" },
  alert:  { bar: "bg-rose-600",    tag: "hs-tag-danger",  label: "ALERT" },
  no_data:{ bar: "bg-slate-300",   tag: "hs-tag-info",    label: "NO DATA" },
};

const STATUS_TAG = {
  GOOD:     "hs-tag-success",
  WATCH:    "hs-tag-warn",
  AT_RISK:  "hs-tag-warn",
  CRITICAL: "hs-tag-danger",
};

function BasicBar({ basic, data }) {
  const pct = data?.percentile;
  const measure = data?.measure;
  const threshold = data?.threshold ?? 65;
  const band = data?.band || "no_data";
  const color = BAND_COLORS[band] || BAND_COLORS.no_data;
  const hasPct = typeof pct === "number";
  const hasMeasure = typeof measure === "number";
  // If we don't have a percentile, derive a visual bar position from the measure
  // value relative to the alert threshold (so the bar still feels meaningful).
  const width = hasPct ? Math.min(Math.max(pct, 2), 100)
                       : (data?.alert ? 90 : (hasMeasure ? 35 : 0));
  const headline = hasPct ? `${pct.toFixed(1)}%`
                         : (data?.alert ? "ALERT" : (hasMeasure ? "OK" : "—"));
  return (
    <div data-testid={`csa-basic-${basic}`} className="hs-card p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="hs-overline text-[10px] text-slate-500">{data?.label || basic}</div>
          <div className="font-display font-bold text-2xl mt-1 font-mono">{headline}</div>
        </div>
        <span className={`hs-tag ${color.tag}`} data-testid={`csa-band-${basic}`}>{color.label}</span>
      </div>
      <div className="relative h-2 bg-slate-100 overflow-hidden rounded">
        <div className={`absolute top-0 left-0 h-full ${color.bar} transition-all`}
             style={{ width: `${width}%` }}></div>
        {hasPct && (
          <div className="absolute top-0 h-full w-px bg-slate-900/40"
               style={{ left: `${threshold}%` }} title={`FMCSA alert at ${threshold}%`}></div>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
        <span>MEASURE: {hasMeasure ? measure.toFixed(2) : "—"}</span>
        <span>{hasPct ? `ALERT @ ${threshold}%` : (data?.alert ? "FMCSA ALERT" : "NO FMCSA ALERT")}</span>
      </div>
    </div>
  );
}

function TrendIcon({ delta }) {
  if (delta == null) return <Minus size={12} className="text-slate-400" />;
  if (delta > 1) return <TrendingUp size={12} className="text-rose-600" />;
  if (delta < -1) return <TrendingDown size={12} className="text-emerald-600" />;
  return <Minus size={12} className="text-slate-400" />;
}

export default function CSAScore() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/csa/snapshot");
      setData(data);
    } catch (e) {
      if (e?.response?.status !== 400) toast.error("Failed to load CSA data");
    } finally { setLoading(false); }
  };

  const loadReports = async () => {
    try {
      const { data } = await api.get("/csa/reports");
      setReports(data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { load(); loadReports(); }, []);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/csa/improvement-report");
      setReport(data);
      loadReports();
      toast.success("AI improvement plan generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report generation failed");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async (reportId) => {
    try {
      const res = await api.get(`/csa/improvement-report/${reportId}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `csa_plan_${reportId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  if (loading) return <div className="hs-overline">LOADING CSA…</div>;

  const snap = data?.snapshot;
  const has = !!data?.has_data;
  const basics = snap?.basics || {};
  const summary = snap?.summary || {};
  const history = (data?.history || []).slice().reverse(); // oldest -> newest

  // Build trend deltas: latest - first available
  const trends = {};
  if (history.length > 1) {
    const first = history[0]?.basics || {};
    const last = history[history.length - 1]?.basics || {};
    Object.keys(last).forEach((k) => {
      const a = first[k]?.percentile, b = last[k]?.percentile;
      if (typeof a === "number" && typeof b === "number") trends[k] = +(b - a).toFixed(1);
    });
  }

  return (
    <div className="space-y-8" data-testid="csa-score-page">
      <div>
        <div className="hs-overline text-blue-700">FMCSA · SAFETY MEASUREMENT SYSTEM</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">My CSA Score</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          Your 7 BASIC percentiles from the official FMCSA SMS monthly dataset. Lower is safer.
          When any BASIC crosses its alert threshold, FMCSA flags your carrier for that category.
        </p>
      </div>

      {!has ? (
        <div className="hs-card p-8 text-center" data-testid="csa-empty-state">
          <ShieldAlert size={32} className="text-slate-400 mx-auto" />
          <h3 className="font-display font-bold text-xl mt-3">No CSA snapshot loaded yet</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-lg mx-auto">
            Your compliance team uploads the official FMCSA SMS monthly file. Once they do,
            your CSA percentile bars will appear here automatically.
          </p>
          {user?.dot_number && (
            <p className="text-xs text-slate-400 mt-3 font-mono">DOT #{user.dot_number}</p>
          )}
        </div>
      ) : (
        <>
          {/* Header card */}
          <div className="hs-card p-6 flex flex-wrap items-start justify-between gap-4" data-testid="csa-header-card">
            <div>
              <div className="hs-overline text-[10px] text-slate-500">SNAPSHOT MONTH</div>
              <div className="font-display font-black text-3xl font-mono mt-1">{snap.snapshot_month}</div>
              <div className="text-xs text-slate-500 mt-2">
                Legal Name: <span className="font-medium text-slate-900">{snap.legal_name || "—"}</span>
                {snap.safety_rating && (
                  <span className="ml-3">Safety Rating:
                    <span className={`ml-1 hs-tag ${snap.safety_rating?.toLowerCase().includes('sat') ? 'hs-tag-success' : 'hs-tag-warn'}`}>
                      {snap.safety_rating}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="hs-overline text-[10px] text-slate-500">BASICs IN ALERT</div>
              <div className={`font-display font-black text-3xl font-mono ${summary.alert_count > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                {summary.alert_count} / 7
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <Gauge size={12} /> {summary.measurable_count} measurable
              </div>
            </div>
          </div>

          {/* BASIC tiles grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="csa-basics-grid">
            {Object.entries(basics).map(([k, v]) => (
              <BasicBar key={k} basic={k} data={v} />
            ))}
          </div>

          {/* Trend strip */}
          {history.length > 1 && (
            <div className="hs-card p-5" data-testid="csa-trend-card">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock size={14} className="text-blue-700" />
                <h3 className="font-display font-bold text-sm uppercase tracking-tight">
                  {history.length}-Month Trend
                </h3>
                <span className="text-[11px] text-slate-500">({history[0].snapshot_month} → {history[history.length - 1].snapshot_month})</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(trends).map(([k, delta]) => (
                  <div key={k} className="flex items-center justify-between border-l-2 border-slate-100 pl-3">
                    <div>
                      <div className="text-[10px] hs-overline text-slate-500">{basics[k]?.label || k}</div>
                      <div className="font-mono text-sm mt-0.5">
                        {delta > 0 ? `+${delta}` : delta}<span className="text-[10px] text-slate-400">pp</span>
                      </div>
                    </div>
                    <TrendIcon delta={delta} />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">↑ trend up = score got worse (higher percentile = more risk)</p>
            </div>
          )}

          {/* AI Improvement Report */}
          <div className="hs-card p-6" data-testid="csa-ai-report-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-blue-700" />
                  <h3 className="font-display font-bold text-xl">AI Improvement Plan</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  Claude analyzes your current snapshot + historical trend and writes a personalized
                  30/60-day action plan with owner-specific tasks.
                </p>
              </div>
              <button
                onClick={generateReport}
                disabled={generating}
                className="hs-btn-primary inline-flex items-center gap-2"
                data-testid="csa-generate-report-btn"
              >
                <Sparkles size={14} /> {generating ? "GENERATING…" : "GENERATE PLAN"}
              </button>
            </div>

            {report && (
              <div className="mt-6 border-t border-slate-200 pt-6 space-y-4" data-testid="csa-current-report">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`hs-tag ${STATUS_TAG[report.plan?.overall_status] || "hs-tag-info"}`}>
                    {report.plan?.overall_status || "—"}
                  </span>
                  <span className="text-sm font-medium">{report.plan?.headline}</span>
                </div>

                {(report.plan?.what_is_working || []).length > 0 && (
                  <div>
                    <div className="hs-overline text-emerald-700 text-[10px]">WHAT&rsquo;S WORKING</div>
                    <ul className="mt-2 text-sm text-slate-700 list-disc pl-5 space-y-1">
                      {report.plan.what_is_working.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}

                {(report.plan?.areas_at_risk || []).length > 0 && (
                  <div>
                    <div className="hs-overline text-rose-700 text-[10px]">AREAS AT RISK</div>
                    <div className="mt-2 space-y-2">
                      {report.plan.areas_at_risk.map((a, i) => (
                        <div key={i} className="border-l-2 border-rose-500 pl-3">
                          <div className="text-sm font-medium">{a.basic}
                            {typeof a.percentile === "number" && (
                              <span className="ml-2 font-mono text-rose-600">{a.percentile.toFixed(1)}%</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600">{a.why}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {["30_day_action_plan", "60_day_action_plan"].map((key) => {
                  const items = report.plan?.[key] || [];
                  if (!items.length) return null;
                  const label = key === "30_day_action_plan" ? "30-DAY ACTION PLAN" : "60-DAY ACTION PLAN";
                  return (
                    <div key={key}>
                      <div className="hs-overline text-blue-700 text-[10px]">{label}</div>
                      <table className="w-full mt-2 text-sm">
                        <thead>
                          <tr className="text-left text-[10px] hs-overline text-slate-500 border-b border-slate-200">
                            <th className="py-2">Action</th><th>Owner</th><th>Effort</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="py-2">{it.action}</td>
                              <td className="text-xs">{it.owner}</td>
                              <td><span className="hs-tag hs-tag-info">{(it.effort || "").toUpperCase()}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}

                {report.plan?.coach_note && (
                  <div className="bg-blue-50 border-l-2 border-blue-700 p-3 text-sm italic text-slate-700">
                    {report.plan.coach_note}
                  </div>
                )}

                <button
                  onClick={() => downloadPdf(report.id)}
                  className="hs-btn-secondary inline-flex items-center gap-2"
                  data-testid="csa-report-pdf-btn"
                >
                  <FileDown size={14} /> DOWNLOAD AS PDF
                </button>
              </div>
            )}

            {reports.length > 0 && (
              <div className="mt-6 border-t border-slate-200 pt-4">
                <div className="hs-overline text-[10px] text-slate-500">PREVIOUS REPORTS</div>
                <ul className="mt-2 space-y-1">
                  {reports.map((r) => (
                    <li key={r.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 font-mono text-xs">
                        {r.snapshot_month} · {(r.created_at || "").slice(0, 10)}
                      </span>
                      <button
                        onClick={() => downloadPdf(r.id)}
                        className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                      >
                        <FileDown size={11} /> PDF
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <p className="text-[11px] text-slate-400 text-center">
            Source: U.S. Department of Transportation · FMCSA Safety Measurement System monthly data.
            Snapshots refresh once per month after FMCSA publishes the next dataset.
          </p>
        </>
      )}
    </div>
  );
}
